import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const SurveyDataTable = ({ communes }) => {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        commune: 'all',
        section: '',
        startDate: '',
        endDate: ''
    });
    const [sections, setSections] = useState([]);
    const [exporting, setExporting] = useState(false);

    // Fetch sections when commune changes (optional, if you have a sections table)
    useEffect(() => {
        if (filters.commune && filters.commune !== 'all') {
            // You can fetch sections from backend by commune
            api.get(`/sections/by-commune?commune=${filters.commune}`)
                .then(res => setSections(res.data.data || []))
                .catch(() => setSections([]));
        } else {
            setSections([]);
        }
    }, [filters.commune]);

    const fetchSurveys = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.commune && filters.commune !== 'all') params.append('commune', filters.commune);
            if (filters.section) params.append('section', filters.section);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            params.append('limit', 1000); // fetch many for display, but could paginate later
            const res = await api.get(`/surveys?${params}`);
            setSurveys(res.data.data || []);
        } catch (err) {
            console.error('Echec du chargement des enquetes :', err);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchSurveys();
    }, [fetchSurveys]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const exportToCSV = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (filters.commune && filters.commune !== 'all') params.append('commune', filters.commune);
            if (filters.section) params.append('section', filters.section);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            params.append('limit', 10000); // large limit
            const res = await api.get(`/surveys?${params}`);
            const data = res.data.data;

            // Prepare CSV headers
            const headers = [
                'ID', 'Enqueteur', 'Repondant', 'Date', 'Commune', 'Section', 'Synchronise',
                'Age', 'Genre', 'Lieu accessible', 'Electricite', 'Mode de paiement', 'Probleme principal', 'Lieu recommande'
            ];
            const rows = data.map(row => [
                row.id, row.surveyor_name, row.respondent_name, row.survey_date,
                row.commune, row.section, row.synced ? 'Oui' : 'Non',
                row.age, row.gender, row.accessible_location, row.electricity,
                row.payment_method, row.biggest_problem, row.recommended_location
            ]);
            const csvContent = [headers, ...rows].map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.setAttribute('download', `enquetes_${new Date().toISOString().slice(0,19)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Echec de l\'exportation :', err);
            alert('Echec de l\'exportation');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="survey-data-table">
            <div className="filters-bar">
                <select name="commune" value={filters.commune} onChange={handleFilterChange}>
                    <option value="all">Toutes les communes</option>
                    {communes.map(c => <option key={c.commune} value={c.commune}>{c.commune}</option>)}
                </select>
                <input type="text" name="section" placeholder="Section" value={filters.section} onChange={handleFilterChange} />
                <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} placeholder="Date de debut" />
                <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} placeholder="Date de fin" />
                <button onClick={fetchSurveys}>Appliquer les filtres</button>
                <button onClick={exportToCSV} disabled={exporting}>{exporting ? 'Exportation...' : 'Exporter CSV'}</button>
            </div>
            {loading && <div className="loading">Chargement des enquetes...</div>}
            {!loading && (
                <div className="table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>ID</th><th>Enqueteur</th><th>Repondant</th><th>Date</th><th>Commune</th><th>Section</th><th>Synchronise</th>
                                <th>Age</th><th>Genre</th><th>Lieu accessible</th><th>Electricite</th><th>Mode de paiement</th><th>Probleme principal</th><th>Lieu recommande</th>
                            </tr>
                        </thead>
                        <tbody>
                            {surveys.map(s => (
                                <tr key={s.id}>
                                    <td>{s.id}</td>
                                    <td>{s.surveyor_name}</td>
                                    <td>{s.respondent_name}</td>
                                    <td>{s.survey_date}</td>
                                    <td>{s.commune}</td>
                                    <td>{s.section}</td>
                                    <td>{s.synced ? '✓' : '✗'}</td>
                                    <td>{s.age}</td>
                                    <td>{s.gender}</td>
                                    <td>{s.accessible_location}</td>
                                    <td>{s.electricity}</td>
                                    <td>{s.payment_method}</td>
                                    <td>{s.biggest_problem}</td>
                                    <td>{s.recommended_location}</td>
                                </tr>
                            ))}
                            {surveys.length === 0 && <tr><td colSpan="14">Aucune enquete trouvee.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SurveyDataTable;